import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useHumhubSessionLogin } from "@/hooks/useHumhubSessionLogin";
import { useOidcTokenRefresh } from "@/hooks/useOidcTokenRefresh";
import { SESSION_REFRESH_RETRY_DELAY_MS } from "@/lib/authSessionConfig";
import { getHumhubExpiresAt, getLatestOidcToken } from "@/lib/authSession";
import {
  buildAuthSessionSnapshot,
  calculateNextSessionRefreshDelay,
  getAdaptiveRefreshBufferMs,
  getNextSessionRefreshAt,
  getPreferredOidcToken,
  isExpiredOrWithinBuffer,
} from "@/lib/authSessionTiming";
import {
  OIDC_MAX_REFRESH_BUFFER_MS,
  OIDC_MIN_REFRESH_BUFFER_MS,
} from "@/lib/authSessionConfig";

export const useSessionRefreshManager = ({ enabled }: { enabled: boolean }) => {
  const auth = useAuth();
  const { loginToHumhubSession } = useHumhubSessionLogin();
  const { refreshAccessToken } = useOidcTokenRefresh();
  const timerRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);
  const isSyncingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleAfter = useCallback(
    (delayMs: number, task: () => void) => {
      clearTimer();
      timerRef.current = window.setTimeout(task, delayMs);
    },
    [clearTimer],
  );

  const getActiveOidcToken = useCallback(
    () =>
      getPreferredOidcToken(
        buildAuthSessionSnapshot(auth),
        getLatestOidcToken(),
      ),
    [auth],
  );

  const syncSession = useCallback(async () => {
    if (!enabled || auth.isLoading || auth.activeNavigator) {
      return;
    }

    if (isSyncingRef.current) {
      return;
    }

    isSyncingRef.current = true;

    try {
      let tokenInfo = getActiveOidcToken();

      if (
        !tokenInfo ||
        isExpiredOrWithinBuffer(
          tokenInfo.expiresAt,
          getAdaptiveRefreshBufferMs(tokenInfo.expiresAt, {
            maxBufferMs: OIDC_MAX_REFRESH_BUFFER_MS,
            minBufferMs: OIDC_MIN_REFRESH_BUFFER_MS,
          }) ?? OIDC_MIN_REFRESH_BUFFER_MS,
        )
      ) {
        tokenInfo = await refreshAccessToken();
      }

      if (!tokenInfo?.token) {
        scheduleAfter(SESSION_REFRESH_RETRY_DELAY_MS, () => {
          void syncSession();
        });
        return;
      }

      await loginToHumhubSession(tokenInfo.token, tokenInfo.expiresAt);

      if (isMountedRef.current) {
        const nextDelay =
          calculateNextSessionRefreshDelay({
            oidcToken: getActiveOidcToken(),
            humhubExpiresAt: getHumhubExpiresAt(),
          }) ?? SESSION_REFRESH_RETRY_DELAY_MS;

        scheduleAfter(nextDelay, () => {
          void syncSession();
        });
      }
    } catch (error) {
      console.error("Session background refresh failed:", error);
      if (isMountedRef.current) {
        scheduleAfter(SESSION_REFRESH_RETRY_DELAY_MS, () => {
          void syncSession();
        });
      }
    } finally {
      isSyncingRef.current = false;
    }
  }, [
    auth.activeNavigator,
    auth.isLoading,
    enabled,
    getActiveOidcToken,
    loginToHumhubSession,
    refreshAccessToken,
    scheduleAfter,
  ]);

  const scheduleNextRefresh = useCallback(() => {
    if (!enabled || auth.isLoading || auth.activeNavigator) {
      clearTimer();
      return;
    }

    const currentHumhubExpiresAt = getHumhubExpiresAt();
    const nextRefreshAt = getNextSessionRefreshAt({
      oidcToken: getActiveOidcToken(),
      humhubExpiresAt: currentHumhubExpiresAt,
    });

    if (!nextRefreshAt) {
      clearTimer();
      return;
    }

    if (Date.now() >= nextRefreshAt) {
      clearTimer();
      void syncSession();
      return;
    }

    const delayMs =
      calculateNextSessionRefreshDelay({
        oidcToken: getActiveOidcToken(),
        humhubExpiresAt: currentHumhubExpiresAt,
      }) ?? SESSION_REFRESH_RETRY_DELAY_MS;

    scheduleAfter(delayMs, () => {
      void syncSession();
    });
  }, [
    auth.activeNavigator,
    auth.isLoading,
    clearTimer,
    enabled,
    getActiveOidcToken,
    scheduleAfter,
    syncSession,
  ]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    scheduleNextRefresh();

    const handleWakeUp = () => {
      scheduleNextRefresh();
    };

    window.addEventListener("visibilitychange", handleWakeUp);
    window.addEventListener("online", handleWakeUp);

    return () => {
      clearTimer();
      window.removeEventListener("visibilitychange", handleWakeUp);
      window.removeEventListener("online", handleWakeUp);
    };
  }, [clearTimer, enabled, scheduleNextRefresh]);
};
